import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./OrderPage.css";
import { useCart } from "../context/CartContext";
import ItemModal from "../components/ItemModal";
import { Helmet } from "react-helmet";

const OrderPage = () => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { successMessage, clearSuccessMessage } = useCart();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 360);
  const [scrollingToCategory, setScrollingToCategory] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 360);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getMenuItemsForCategory = (categoryId) =>
    menuItems.filter((item) => Number(item.category_id) === Number(categoryId));

  const sortCategories = (categoryList) => {
    const startersId = "1",
      mainMenuId = "17",
      drinksId = "16",
      preorderId = "6";
    return categoryList.sort((a, b) => {
      if (a.id === startersId) return -1;
      if (b.id === startersId) return 1;
      if (a.id === mainMenuId) return -1;
      if (b.id === mainMenuId) return 1;
      if (a.id === drinksId) return 1;
      if (b.id === drinksId) return -1;
      if (a.id === preorderId) return 1;
      if (b.id === preorderId) return -1;
      return a.category_name.localeCompare(b.category_name);
    });
  };

  const getFilteredCategories = () => {
    if (!searchQuery) return sortCategories(categories);
    const lowerCaseQuery = searchQuery.toLowerCase();
    const categoryIndex = new Map();

    categories.forEach((category) => {
      let itemNames = getMenuItemsForCategory(category.id).map((item) =>
        item.item_name.toLowerCase()
      );
      if (category.subcategories) {
        category.subcategories.forEach((sub) => {
          const subItems = getMenuItemsForCategory(sub.id).map((item) =>
            item.item_name.toLowerCase()
          );
          itemNames = itemNames.concat(subItems);
        });
      }
      categoryIndex.set(category.id, itemNames);
    });

    return sortCategories(
      categories.filter((category) =>
        categoryIndex
          .get(category.id)
          ?.some((itemName) => itemName.includes(lowerCaseQuery))
      )
    );
  };

  const filteredCategories = getFilteredCategories();

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchCategories(), fetchMenuItems()]);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const scrollToCategory = useCallback(
    (categoryId) => {
      const el = document.getElementById(`category-${categoryId}`);
      if (el) {
        const offset = el.offsetTop - (isMobile ? 190 : 130);
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    },
    [isMobile]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryFromUrl = urlParams.get("category");
    if (!categoryFromUrl || categories.length === 0) return;

    const matchedCategory = categories.find(
      (category) =>
        category?.category_name?.trim().toLowerCase() ===
        categoryFromUrl.trim().toLowerCase()
    );

    if (matchedCategory) {
      setActiveCategory(matchedCategory.id);
      setTimeout(() => scrollToCategory(matchedCategory.id), 300);
    }
  }, [location.search, categories, scrollToCategory]);

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        clearSuccessMessage();
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, clearSuccessMessage]);

  const fetchCategories = async () => {
    try {
      const response = await fetch("/php/categories.php");
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await fetch("/php/menu-items.php");
      const data = await response.json();
      setMenuItems(data);
    } catch (err) {
      console.error("Error fetching menu items:", err);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    if (e.target.value === "") setActiveCategory(null);
  };

  const handleCategoryClick = (id) => {
    setScrollingToCategory(true);
    setActiveCategory(id);
    scrollToCategory(id);
    setTimeout(() => setScrollingToCategory(false), 800);
  };

  useEffect(() => {
    if (filteredCategories.length > 0 && !activeCategory) {
      const firstId = filteredCategories[0].id;
      setActiveCategory(firstId);
    }
  }, [filteredCategories, activeCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollingToCategory) return;
      const NAVBAR_HEIGHT = 100;
      const scrollPosition = window.scrollY + NAVBAR_HEIGHT + 20;

      const visibleCategory = filteredCategories.find((category) => {
        const el = document.getElementById(`category-${category.id}`);
        if (!el) return false;
        const top = el.offsetTop;
        const height = el.offsetHeight;
        return scrollPosition >= top && scrollPosition < top + height;
      });

      if (visibleCategory && activeCategory !== visibleCategory.id) {
        setActiveCategory(visibleCategory.id);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredCategories, activeCategory, scrollingToCategory]);

  const handleAddToCart = (item) => setSelectedItem(item);

  return (
    <div className="order-page-container page-container">
      <Helmet>
        <title>Order Online | LasGidi Restaurant</title>
        <meta
          name="description"
          content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway."
        />
        <meta
          name="keywords"
          content="LasGidi Restaurant, Order Online, Nigerian Food Delivery, Manchester Takeaway, Food Delivery"
        />
        <meta property="og:title" content="Order Online | LasGidi Restaurant" />
        <meta
          property="og:description"
          content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {showSuccessMessage && successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="order-container heading-container">
        <h1>Our Menu</h1>
      </div>

      {isMobile && (
        <div className="mobile-category-dropdown">
          <select
            value={activeCategory || ""}
            onChange={(e) => handleCategoryClick(e.target.value)}
          >
            {filteredCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.category_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="order-page-content sidebar-layout">
        {!isMobile && (
          <aside className="category-sidebar">
            <div className="search-input-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Search items..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>

            <ul className="category-list">
              {filteredCategories.map((category) => (
                <li
                  key={category.id}
                  className={`category-link ${
                    activeCategory === category.id ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {category.category_name}
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div className="main-menu-section">
          <p className="payment-notice">
            🚨 PAY BY TRANSFER OR AT THE RESTAURANT
          </p>
          <p className="order-notice allergy-notice">
            For allergies, please contact us.
          </p>

          {isMobile && (
            <div className="search-input-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Search items..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          )}

          {isLoading && <p className="no-items">Loading items...</p>}
          {categories.length === 0 && !isLoading && (
            <p className="no-items">No categories found</p>
          )}
          {searchQuery && filteredCategories.length === 0 && (
            <p className="no-items">No results found for "{searchQuery}"</p>
          )}

          {filteredCategories.map((category) => {
            const items = getMenuItemsForCategory(category.id).filter((item) =>
              item.item_name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            const subcategories = category.subcategories || [];
            const filteredSubcategories = subcategories.filter((sub) =>
              getMenuItemsForCategory(sub.id).some((item) =>
                item.item_name.toLowerCase().includes(searchQuery.toLowerCase())
              )
            );

            return (
              <div
                key={category.id}
                id={`category-${category.id}`}
                className="category-section"
              >
                <h2 className="category-heading">{category.category_name}</h2>
                {items.length === 0 && filteredSubcategories.length === 0 && (
                  <p className="no-items">No items available</p>
                )}
                {items.length > 0 && (
                  <ul className="order-menu-list">
                    {items.map((item) => (
                      <li key={item.id} className="order-menu-item">
                        <img
                          src={`https://lasgidirestaurant.com/${item.image}`}
                          alt={item.item_name}
                          className="order-menu-item-image"
                        />
                        <div className="order-menu-item-details">
                          <h3>{item.item_name}</h3>
                          <p className="menu-item-description">
                            {item.item_description}
                          </p>
                          {Number(item.in_stock) === 0 && (
                            <div className="out-of-stock-ticker">
                              <span>Sold Out</span>
                            </div>
                          )}
                          <div className="menu-item-bottom">
                            {item.price > 0 && (
                              <p className="menu-item-price">£{item.price}</p>
                            )}
                            <div
                              className="add-to-cart-icon"
                              onClick={() => handleAddToCart(item)}
                            >
                              <i className="fas fa-shopping-cart cart-btn"></i>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {filteredSubcategories.map((sub) => {
                  const subItems = getMenuItemsForCategory(sub.id).filter(
                    (item) =>
                      item.item_name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                  );

                  return (
                    <div key={sub.id} className="subcategory">
                      <h3 className="subcategory-heading">
                        {sub.category_name}
                      </h3>
                      <ul className="order-menu-list">
                        {subItems.map((item) => (
                          <li key={item.id} className="order-menu-item">
                            <img
                              src={`https://lasgidirestaurant.com/${item.image}`}
                              alt={item.item_name}
                              className="order-menu-item-image"
                            />
                            <div className="order-menu-item-details">
                              <h3>{item.item_name}</h3>
                              <p className="menu-item-description">
                                {item.item_description}
                              </p>
                              {Number(item.in_stock) === 0 && (
                                <div className="out-of-stock-ticker">
                                  <span>Sold Out</span>
                                </div>
                              )}
                              <div className="menu-item-bottom">
                                {item.price > 0 && (
                                  <p className="menu-item-price">
                                    £{item.price}
                                  </p>
                                )}
                                <div
                                  className="add-to-cart-icon"
                                  onClick={() => handleAddToCart(item)}
                                >
                                  <i className="fas fa-shopping-cart cart-btn"></i>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {selectedItem && (
        <ItemModal
          isOpen={!!selectedItem}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSuccess={() => setShowSuccessMessage(true)}
        />
      )}
    </div>
  );
};

export default OrderPage;
